// Component for the table view in the file explorer (right side).
// It contains the list of files, the context menu for each file and folder,
// and all the associated logic for uploading, downloading, importing and exporting files.

import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { DataGridPro, GridRowModel } from "@mui/x-data-grid-pro";
import { useContext, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import {
  ExtendedRepositoryObject,
  RepositoryObject,
  RepositoryObjectFolder
} from "../../backend/types";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { removeTags, shortenString } from "../../helpers/stringHelper";
import { columnsRender, MenuContext } from "../../helpers/tableViewHelper";
import { useTableViewHook } from "../../hooks/tableViewHook";
import { useUserHelper } from "../../hooks/userHelper";
import ErrorBoundary from "../abstractComponents/errorBoundary";
import { useAuth } from "../authContextProvider";
import CompressModal from "../contextMenu/fileOperations/compressModal";
import CopyMoveModal from "../contextMenu/fileOperations/copyMoveModal";
import HideModal from "../contextMenu/fileOperations/hideModal";
import RenameModal from "../contextMenu/fileOperations/renameModal";
import UncompressModal from "../contextMenu/fileOperations/uncompressModal";
import CreateExternalDownloadLink from "../externalLink/createExternalDownloadLink";
import { TableViewListFilesInRepoNavbar } from "../importExportMenu/tableViewListFilesInRepoNavbar";

const MAX_COMMENT_MENU_LENGTH = 50;

interface TableViewListFilesInRepoProps {
  areRepoObjectsLoading: boolean;
  createFolderMutation: Function;
  currentSelectedPath: string;
  currentSelectedRepoId: string;
  currentTreeRepositoryObjects: RepositoryObject[];
  foldersAndCount: RepositoryObjectFolder[];
  forceRefreshPmxActivity: Function;
  getRepositoryObjects: Function;
  isCreateFolderWindowOpen: boolean;
  onNodeSelect: Function;
  refetchRepositoryObjects: Function;
  setCurrentSelectedRepoId: Function;
  setCurrentSelectedVersionId: Function;
  setIsActionOfFilesVisible: Function;
  setIsCopyMoveModalOpenHook: Function;
  setIsCreateFolderWindowOpen: Function;
  setIsEditActionVisible: Function;
  setIsProcessingExport: Function;
  setIsTracebilityReportVisible: Function;
  setSelectedCommentIdFromMenu: Function;
  setTableMenuWasOpenOnPath: Function;
  tableMenuWasOpenOnPath: string;
  tableViewFiles: RepositoryObject[];
}

export function TableViewListFilesInRepo(props: TableViewListFilesInRepoProps) {
  const { activityId, activityData } = useContext(PmxActivityDetailsContext);
  const [readOnlyCopyAction, setReadOnlyCopyAction] = useState<boolean>(false);
  const folderInfo = props?.foldersAndCount.find(
    (folder: RepositoryObjectFolder) =>
      folder.folderName === props.currentSelectedPath
  )!;

  const {
    selectedRows,
    downloadMultipleFiles,
    handleSelectMultipleRows,
    anchorElFolder,
    handleCloseFolder,
    anchorElFile,
    currentCommentsForSelectedFile,
    handleCloseCommentMenu,
    isFileContextMenuOpen,
    handleCloseFileMenu,
    handleOpenProperties,
    handleOpenTracebilityReport,
    folderName,
    setFolderName,
    isFolderNameError,
    setIsFolderNameError,
    createFolder,
    setCurrentCommentsForSelectedFile,
    hideObjects,
    isHideModalOpen,
    closeHideModal,
    isCompressModalOpen,
    setIsCompressModalOpen,
    isUncompressModalOpen,
    setIsUncompressModalOpen,
    hideComment,
    setHideComment,
    openHideModal,
    isRenameModalOpen,
    setIsRenameModalOpen,
    openRenameModal,
    handleClickFile,
    handleClickComment,
    handleClickFolder,
    handleOpenEditAction,
    setIsCopyMoveModalOpen,
    isCopyMoveModalOpen,
    openCopyMoveModal,
    copyOrMoveAction,
    handleOpenExternalDownloadLink,
    isDownloadExternalLinkVisible,
    onCloseExternalDownloadModal,
    setIsDownloadExternalLinkVisible,
    openCompressModal,
    openUncompressModal,
    setSelectedRows,
    errorMutationHide,
    isLoadingMutationHide,
    extendTableRecordDetails,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel
  } = useTableViewHook(
    props.getRepositoryObjects,
    folderInfo,
    props.currentSelectedPath,
    props.currentSelectedRepoId,
    activityData,
    props.refetchRepositoryObjects,
    props.tableMenuWasOpenOnPath,
    props.createFolderMutation,
    props.setTableMenuWasOpenOnPath,
    props.setIsActionOfFilesVisible,
    props.setIsTracebilityReportVisible,
    props.setIsEditActionVisible,
    props.tableViewFiles, // only file list for table view,
    props.areRepoObjectsLoading
  );
  const { isBayerUser } = useUserHelper();
  const { decodedToken } = useAuth();

  const consolidateEditActionCommands = {
    setCurrentSelectedVersionId: props.setCurrentSelectedVersionId,
    handleOpenEditAction: handleOpenEditAction
  };

  const isMainRepositorySelected =
    extendTableRecordDetails.some(
      (row) => row.repository.id === activityData?.mainRepository?.id
    ) || activityData?.mainRepository?.id === props.currentSelectedRepoId;

  const rowsForCurrentSelectedPath =
    !props.areRepoObjectsLoading && folderInfo
      ? (extendTableRecordDetails as readonly GridRowModel[])
      : [];

  const rowCountForCurrentSelectedPath =
    rowsForCurrentSelectedPath.length === 0 ? 0 : folderInfo?.count ?? 0;

  return (
    <>
      <TableViewListFilesInRepoNavbar
        activityId={activityId}
        currentSelectedPath={props.currentSelectedPath}
        currentSelectedRepoId={props.currentSelectedRepoId}
        currentTableRows={extendTableRecordDetails}
        downloadMultipleFiles={downloadMultipleFiles}
        forceRefreshPmxActivity={props.forceRefreshPmxActivity}
        isCreateFolderWindowOpen
        isMainRepositorySelected={isMainRepositorySelected}
        refetchRepositoryObjects={props.refetchRepositoryObjects}
        selectedRows={selectedRows}
        setIsCreateFolderWindowOpen={props.setIsCreateFolderWindowOpen}
        setIsDownloadExternalLinkVisible={setIsDownloadExternalLinkVisible}
      />
      <MenuContext.Provider
        value={{
          handleClickFolder,
          handleClickFile,
          handleClickComment,
          onNodeSelect: props.onNodeSelect
        }}
      >
        <ErrorBoundary>
          <div className="table-view-list-container">
            <DataGridPro
              checkboxSelection
              className="browser_repository_table"
              classes={
                extendTableRecordDetails[0]?.repository.id !==
                activityData.mainRepository.id
                  ? {
                      virtualScroller: "opacity-60"
                    }
                  : {}
              }
              columns={columnsRender(
                () => {
                  props.refetchRepositoryObjects();
                },
                extendTableRecordDetails.length === 0,
                true,
                false,
                consolidateEditActionCommands
              )}
              components={{
                NoResultsOverlay: () => (
                  <Stack
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    Local filter returns no result
                  </Stack>
                ),
                NoRowsOverlay: () => (
                  <Stack
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    No rows.
                    {(!props.currentTreeRepositoryObjects ||
                      props.currentTreeRepositoryObjects.length === 0) &&
                      "It is possible that the system is processing new files. If that is the case, please refresh again in a few minutes."}
                  </Stack>
                )
              }}
              disableSelectionOnClick
              disableVirtualization={decodedToken["email"].includes(
                "test-user@bayer.com"
              )}
              filterMode="server"
              filterModel={filterModel}
              getRowHeight={() => "auto"}
              getRowId={(row) => row.absolutePath}
              loading={props.areRepoObjectsLoading}
              onFilterModelChange={(newFilterModel) =>
                setFilterModel(newFilterModel)
              }
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(pageSize) => setPageSize(pageSize)}
              onSelectionModelChange={handleSelectMultipleRows}
              onSortModelChange={(model) => setSortModel(model)}
              page={page}
              pageSize={pageSize}
              pagination
              paginationMode={"server"}
              rowCount={rowCountForCurrentSelectedPath}
              rows={rowsForCurrentSelectedPath}
              rowsPerPageOptions={[25, 50, 100]}
              selectionModel={selectedRows}
              sortModel={sortModel ? sortModel : []}
              sortingMode="server"
            />
          </div>
        </ErrorBoundary>
      </MenuContext.Provider>
      <Menu
        className="context_menu_folder"
        id="simple-menu"
        anchorEl={anchorElFolder}
        open={Boolean(anchorElFolder)}
        onClose={handleCloseFolder}
        PaperProps={{
          style: {
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
            marginTop: "15px",
            marginLeft: "5px"
          }
        }}
      >
        {isMainRepositorySelected && (
          <div>
            {props.tableMenuWasOpenOnPath && (
              <MenuItem onClick={openRenameModal} id="rename_menu_context_item">
                Rename
              </MenuItem>
            )}
            <MenuItem
              onClick={() => openCopyMoveModal("move")}
              id="context-menu__move-item-folder"
            >
              Move{selectedRows.length !== 0 && " Selected Folder(s)"}
            </MenuItem>
            <MenuItem onClick={openHideModal}>
              Hide{selectedRows.length !== 0 && " Selected Folder(s)"}
            </MenuItem>
          </div>
        )}
        <MenuItem
          onClick={() => {
            openCopyMoveModal("copy");
            setReadOnlyCopyAction(!isMainRepositorySelected);
          }}
          id="context-menu__copy-item-folder"
        >
          Copy{selectedRows.length !== 0 && " Selected Folder(s)"}
        </MenuItem>
        <MenuItem onClick={openCompressModal} id="context_menu_compress_folder">
          Compress{selectedRows.length !== 0 && " Selected Folder(s)"}
        </MenuItem>
      </Menu>
      <Menu
        className="context_menu_comment"
        id="simple-menu"
        anchorEl={anchorElFile}
        open={Boolean(
          currentCommentsForSelectedFile &&
            currentCommentsForSelectedFile.length
        )}
        onClose={handleCloseCommentMenu}
        PaperProps={{
          style: {
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
            marginTop: "15px",
            marginLeft: "5px"
          }
        }}
      >
        {currentCommentsForSelectedFile &&
          currentCommentsForSelectedFile.map((comment, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                props.setSelectedCommentIdFromMenu(comment.id);
                setCurrentCommentsForSelectedFile([]);
              }}
              className="comment_in_menu_table_view"
            >
              {shortenString(removeTags(comment.text), MAX_COMMENT_MENU_LENGTH)}
              <br />
              <div className="comment_user_details">
                {comment.createdBy} - {comment.created}{" "}
              </div>
            </MenuItem>
          ))}
      </Menu>
      <Menu
        className="context_menu_file"
        id="simple-menu"
        anchorEl={anchorElFile}
        open={isFileContextMenuOpen}
        onClose={handleCloseFileMenu}
        PaperProps={{
          style: {
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
            marginTop: "15px",
            marginLeft: "5px"
          }
        }}
      >
        <MenuItem
          onClick={downloadMultipleFiles}
          id="context_menu_download_file"
        >
          Download{selectedRows.length !== 0 && " Selected File(s)"}
        </MenuItem>
        {selectedRows.length === 0 &&
          props.tableMenuWasOpenOnPath.endsWith(".zip") && (
            <MenuItem onClick={openUncompressModal} id="uncompress_menu_item">
              Uncompress
            </MenuItem>
          )}

        <MenuItem
          disabled={!activityData?.mainRepository?.isStandard}
          onClick={() => handleOpenExternalDownloadLink()}
        >
          Generate External Download Link
          {selectedRows.length !== 0 && " For Selected File(s)"}
        </MenuItem>
        <Divider />

        {isMainRepositorySelected && (
          <div>
            <MenuItem onClick={openHideModal}>
              Hide{selectedRows.length !== 0 && " Selected File(s)"}
            </MenuItem>
            {props.tableMenuWasOpenOnPath && selectedRows.length <= 1 && (
              <MenuItem onClick={openRenameModal} id="context_menu_rename_file">
                Rename
              </MenuItem>
            )}
            <MenuItem
              onClick={() => openCopyMoveModal("move")}
              id="context-menu__move-item-file"
            >
              Move{selectedRows.length !== 0 && " Selected File(s)"}
            </MenuItem>
          </div>
        )}
        <MenuItem
          onClick={() => {
            openCopyMoveModal("copy");
            setReadOnlyCopyAction(isMainRepositorySelected ? false : true);
          }}
          id="context-menu__copy-item-file"
        >
          Copy{selectedRows.length !== 0 && " Selected File(s)"}
        </MenuItem>
        {isMainRepositorySelected && (
          <MenuItem onClick={openCompressModal} id="context_menu_compress_file">
            Compress
            {selectedRows.length !== 0 && " Selected File(s)"}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleOpenEditAction}>Creating Action</MenuItem>
        {isBayerUser && (
          <MenuItem onClick={handleOpenTracebilityReport}>
            Traceability Report
          </MenuItem>
        )}
        <MenuItem
          onClick={handleOpenProperties}
          className="properties_file_button"
        >
          Properties
        </MenuItem>
      </Menu>
      <Modal
        show={props.isCreateFolderWindowOpen}
        onHide={() => props.setIsCreateFolderWindowOpen(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-break">
            Create new Folder in {props.currentSelectedPath}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="formBasicName">
            <Form.Label>
              Name <span className="red_form">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              required
              placeholder="Enter new folder name"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value.replace("/", ""));
                setIsFolderNameError(false);
              }}
              isInvalid={isFolderNameError}
            />
            <Form.Control.Feedback type="invalid">
              A folder with this name already exists in this directory
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => props.setIsCreateFolderWindowOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!folderName}
            onClick={createFolder}
            id="modalCreateFolderButton"
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>
      <HideModal
        closeHideModal={closeHideModal}
        errorMutationHide={errorMutationHide}
        hideComment={hideComment}
        hideObjects={hideObjects}
        isHideModalOpen={isHideModalOpen}
        isLoading={isLoadingMutationHide}
        selectedRows={selectedRows}
        setHideComment={setHideComment}
        tableMenuWasOpenOnPath={props.tableMenuWasOpenOnPath}
      />
      <CompressModal
        closeCompressModal={() => {
          setIsCompressModalOpen(false);
        }}
        currentSelectedPath={props.currentSelectedPath}
        currentTableRows={extendTableRecordDetails}
        isCompressModalOpen={isCompressModalOpen}
        refetchRepositoryObjects={() => {
          props.refetchRepositoryObjects();
          setSelectedRows([]);
        }}
        selectedRows={
          selectedRows.length === 0
            ? [props.tableMenuWasOpenOnPath]
            : selectedRows
        }
      />
      <UncompressModal
        closeUncompressModal={() => {
          setIsUncompressModalOpen(false);
        }}
        currentSelectedRepoId={props.currentSelectedRepoId}
        isUncompressModalOpen={isUncompressModalOpen}
        refetchRepositoryObjects={() => {
          props.refetchRepositoryObjects();
        }}
        tableMenuWasOpenOnPath={props.tableMenuWasOpenOnPath}
        versionId={
          extendTableRecordDetails.find(
            (file: ExtendedRepositoryObject) =>
              file.absolutePath === props.tableMenuWasOpenOnPath
          )?.versionId
        }
      />
      <RenameModal
        activityId={activityId}
        currentSelectedPath={props.currentSelectedPath}
        currentTableRows={extendTableRecordDetails}
        isRenameModalOpen={isRenameModalOpen}
        refetchRepositoryObjects={() => {
          props.refetchRepositoryObjects();
        }}
        selectedRows={selectedRows}
        setIsRenameModalOpen={setIsRenameModalOpen}
        tableMenuWasOpenOnPath={props.tableMenuWasOpenOnPath}
      />

      <CopyMoveModal
        actionType={copyOrMoveAction}
        activityData={activityData}
        areRepoObjectsLoading={props.areRepoObjectsLoading}
        currentSelectedPath={props.currentSelectedPath}
        currentSelectedRepoId={props.currentSelectedRepoId}
        currentTableRows={extendTableRecordDetails}
        isCopyMoveModalOpen={isCopyMoveModalOpen}
        onNodeSelect={props.onNodeSelect}
        readOnlyCopyAction={readOnlyCopyAction}
        refetchRepositoryObjects={() => {
          props.refetchRepositoryObjects();
        }}
        selectedRows={
          selectedRows.length > 0
            ? selectedRows
            : [props.tableMenuWasOpenOnPath]
        }
        setCurrentSelectedRepoId={props.setCurrentSelectedRepoId}
        setIsCreateFolderWindowOpen={props.setIsCreateFolderWindowOpen}
        setIsCopyMoveModalOpen={setIsCopyMoveModalOpen}
        setIsCopyMoveModalOpenHook={props.setIsCopyMoveModalOpenHook}
      />
      {isDownloadExternalLinkVisible && (
        <ErrorBoundary>
          <CreateExternalDownloadLink
            activityId={activityId}
            currentSelectedRepoId={props.currentSelectedRepoId}
            currentSelectedPath={props.currentSelectedPath}
            currentTableRows={extendTableRecordDetails}
            onCloseExternalDownloadModal={onCloseExternalDownloadModal}
            selectedRows={
              selectedRows.length > 0
                ? selectedRows
                : [props.tableMenuWasOpenOnPath]
            }
          />
        </ErrorBoundary>
      )}
    </>
  );
}
